export function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException) {
    return error.name === 'AbortError'
  }
  if (error instanceof Error) {
    if (error.name === 'CanceledError' || error.name === 'AbortError') return true
  }
  return typeof error === 'object' && error !== null && 'code' in error && (error as { code?: unknown }).code === 'ERR_CANCELED'
}

export interface LatestRequestManager {
  run<T>(key: string, runner: (signal: AbortSignal) => Promise<T>): Promise<T>
  cancel(key: string): void
  cancelAll(): void
}

export function createLatestRequestManager(): LatestRequestManager {
  const controllers = new Map<string, AbortController>()

  function cancel(key: string) {
    const controller = controllers.get(key)
    if (!controller) return
    controller.abort()
    controllers.delete(key)
  }

  async function run<T>(key: string, runner: (signal: AbortSignal) => Promise<T>): Promise<T> {
    cancel(key)
    const controller = new AbortController()
    controllers.set(key, controller)
    try {
      return await runner(controller.signal)
    } finally {
      if (controllers.get(key) === controller) {
        controllers.delete(key)
      }
    }
  }

  function cancelAll() {
    for (const controller of controllers.values()) {
      controller.abort()
    }
    controllers.clear()
  }

  return { run, cancel, cancelAll }
}
